<template>
  <div style="margin:20px">
    <FiPageRail title="Vehicle Status History">
      <template #actions>
        <span class="actuals-header-actions">
          <b-button size="sm" variant="success"
                    title="Download CSV for the current range and filters"
                    aria-label="Download CSV for the current range and filters"
                    v-on:click.prevent="download()">
            <i class="fa fa-download" aria-hidden="true"></i>
          </b-button>
          <vsm-history-help />
        </span>
      </template>
    </FiPageRail>

    <!-- dates on their own row, laid out like the actuals toolbar; the status chips
         stay in the chip row below -->
    <div v-if="summary" class="actuals-cm-toolbar vsh-toolbar">
      <div class="actuals-date-presets" role="group" aria-label="Date range">
        <span class="actuals-range-label">Range</span>
        <button v-for="opt in RANGES.filter(r => r.value !== 'custom')" :key="'qr-' + opt.value"
                type="button"
                :class="['actuals-date-chip', {'actuals-date-chip--active': active_range_days === opt.value}]"
                :aria-pressed="active_range_days === opt.value ? 'true' : 'false'"
                :title="opt.title"
                v-on:click.prevent="set_range(opt.value)">{{ opt.label }}</button>
      </div>

      <div class="actuals-filter-dates actuals-filter-dates--open">
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Previous day" v-on:click.prevent="shift_detail_graph_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Next day" v-on:click.prevent="shift_detail_graph_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
        <b-form-select v-if="vehicle_tag_options && vehicle_tag_options.length > 1" size="sm"
                       v-model="selected_tag" :options="vehicle_tag_options" class="toolbar-select"/>
        <b-form-select size="sm" v-model="selected_vehicle" :options="vehicle_options" class="toolbar-select"/>
        <b-button size="sm" v-on:click.prevent="load_data()">Search</b-button>
      </div>
    </div>

    <div v-if="summary" style="margin: 0 8px 10px">
      <vsm-mobile-filter-chips
        :size="isMobile() ? 'sm' : 'md'"
        :total-format="isMobile() ? 'slash' : 'of'"
        :items-count="items.length"
        :total-count="summary.total_cnt"
        :total-selected="!selected_status && !selected_maint_status && !search_filter"
        :show-search="true"
        :search-filter="search_filter"
        search-placeholder="filter by keyword"
        @clear-filters="clear_history_filters()"
        @update:searchFilter="search_filter = $event"
      >
        <span class="vsm-chip vsm-chip-label">Status</span>
        <!-- a tenant without wash tracking has no Ready/Washed rows to find, but show the
             chip anyway if the window happens to hold some rather than stranding them -->
        <span v-if="summary.washed_enabled || status_counts.READY" class="vsm-chip vsm-chip-clickable ready" :class="{ 'vsm-chip-selected': selected_status === 'READY' }" @click="filter_by_status('ready')">Ready ({{ status_counts.READY }})</span>
        <span class="vsm-chip vsm-chip-clickable dirty" :class="{ 'vsm-chip-selected': selected_status === 'DIRTY' }" @click="filter_by_status('dirty')">Dirty ({{ status_counts.DIRTY }})</span>
        <span class="vsm-chip vsm-chip-clickable cleaned" :class="{ 'vsm-chip-selected': selected_status === 'CLEAN' }" @click="filter_by_status('cleaned')">Clean ({{ status_counts.CLEAN }})</span>
        <span v-if="summary.washed_enabled || status_counts.WASH" class="vsm-chip vsm-chip-clickable washed" :class="{ 'vsm-chip-selected': selected_status === 'WASH' }" @click="filter_by_status('washed')">Washed ({{ status_counts.WASH }})</span>
        <span class="vsm-chip vsm-chip-clickable out" :class="{ 'vsm-chip-selected': selected_status === 'OUT' }" @click="filter_by_status('out')">Out ({{ status_counts.OUT }})</span>
        <template v-if="show_maint_chips">
          <span class="vsm-chip vsm-chip-label">Maint</span>
          <span class="vsm-chip vsm-chip-clickable ready" :class="{ 'vsm-chip-selected': selected_maint_status === 'READY' }" @click="filter_by_maint_status('READY')">Ready ({{ maint_counts.READY }})</span>
          <span v-if="dvir_combined" class="vsm-chip vsm-chip-clickable dvir" :class="{ 'vsm-chip-selected': selected_maint_status === 'DVIR' }" @click="filter_by_maint_status('DVIR')">DVIR ({{ maint_counts.DVIR }})</span>
          <template v-else>
            <span class="vsm-chip vsm-chip-clickable pending_dvir" :class="{ 'vsm-chip-selected': selected_maint_status === 'PENDING_DVIR' }" @click="filter_by_maint_status('PENDING_DVIR')">Pending ({{ maint_counts.PENDING_DVIR }})</span>
            <span class="vsm-chip vsm-chip-clickable unsafe_dvir" :class="{ 'vsm-chip-selected': selected_maint_status === 'UNSAFE_DVIR' }" @click="filter_by_maint_status('UNSAFE_DVIR')">Unsafe ({{ maint_counts.UNSAFE_DVIR }})</span>
          </template>
          <span class="vsm-chip vsm-chip-clickable pm" :class="{ 'vsm-chip-selected': selected_maint_status === 'PM' }" @click="filter_by_maint_status('PM')">PM ({{ maint_counts.PM }})</span>
          <span class="vsm-chip vsm-chip-clickable oos" :class="{ 'vsm-chip-selected': selected_maint_status === 'OOS' }" @click="filter_by_maint_status('OOS')">OOS ({{ maint_counts.OOS }})</span>
        </template>
      </vsm-mobile-filter-chips>
    </div>

    <div v-if="summary">
      <b-table :busy="loading" :sticky-header="windowHeight ? windowHeight*.9 + 'px':'80vh'" bordered :items="items" :fields="visibleFields"
               :current-page="currentPage" :sort-compare="tableSortCompare" :style="'font-size:' + summary.font_size + 'px'" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
          </div>
        </template>

        <template slot="notes" slot-scope="row">
          <div style="text-align: left">{{row.item.notes}}</div>
        </template>

        <template slot="vehicle_status" slot-scope="row">
          {{row.item.vehicle_status}}
        </template>
        <template slot="maintenance_status" slot-scope="row">
          {{display_maint_status(row.item.maintenance_status)}}
        </template>
        <!-- the board's expanded-history markup, so a change reads the same in both places -->
        <template slot="changes" slot-scope="row">
          <span class="vst-hist-changes vsh-changes">
            <template v-if="row.item.changes && row.item.changes.length">
              <span v-for="(c, ci) in row.item.changes" :key="ci" class="vst-hist-change">
                <span class="vst-hist-change-label">{{ c.label }}</span>
                <span class="vst-hist-change-old">{{ c.from }}</span>
                <i class="fa fa-long-arrow-right vst-hist-change-arrow" aria-hidden="true"></i>
                <span class="vst-hist-change-new">{{ c.to }}</span>
              </span>
            </template>
            <span v-else-if="row.item.no_earlier" class="vst-hist-nochange">first record in range</span>
            <span v-else class="vst-hist-nochange">no field changed in this update</span>
          </span>
        </template>
        <template slot="confirmed_status" slot-scope="row">
          <div v-if="row.item.confirmed_time" style="font-weight: bold; background-color: lightgreen">
            {{row.item.confirmed_time | formatDateTimeSmall}}
          </div>
          <div v-else-if="row.item.confirmed_status == 'sent'" style="font-weight: bold; background-color: lightsalmon">
            {{row.item.confirmed_sent_time | formatDateTimeSmall}}
          </div>
        </template>
        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
        </template>
        <template slot="last_dirty_time" slot-scope="row">
          {{row.item.last_dirty_time | formatDateTimeSmall}}
        </template>
        <template slot="last_cleaned_time" slot-scope="row">
          {{row.item.last_cleaned_time | formatDateTimeSmall}}
        </template>
        <template slot="last_washed_time" slot-scope="row">
          {{row.item.last_washed_time | formatDateTimeSmall}}
        </template>
        <template slot="last_ready_time" slot-scope="row">
          {{row.item.last_ready_time | formatDateTimeSmall}}
        </template>
        <template slot="detailed_date" slot-scope="row">
          {{row.item.detailed_date | formatDateTimeSmall}}
        </template>
        <template slot="wrapped_date" slot-scope="row">
          {{row.item.wrapped_date | formatDateTimeSmall}}
        </template>
        <template slot="dump_date" slot-scope="row">
          {{row.item.dump_date | formatDateTimeSmall}}
        </template>
        <template slot="last_out_time" slot-scope="row">
          {{row.item.last_out_time | formatDateTimeSmall}}
        </template>
        <template slot="last_returned_time" slot-scope="row">
          {{row.item.last_returned_time | formatDateTimeSmall}}
        </template>

      </b-table>
      <b-row v-if="items.length > 10">
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import {api_url, get_tag_list, get_vehicle_list, get_vehicle_status_history} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment';
import {justDate} from "../helpers/dates";
import axios from "axios";
import { displayMaintStatus } from '../utils/vsm_fields'
import VsmMobileFilterChips from './vsm_mobile_filter_chips.vue'
import VsmHistoryHelp from './vsm_history_help.vue'
import { RANGES } from '../utils/date_ranges'
import { annotateChanges } from '../utils/vsm_status_changes'

export default {
  name: 'vehicle_status_history',
  components: {
     VsmMobileFilterChips,
     VsmHistoryHelp,
  },
  metaInfo: {
    title: "Fleet Integrations",
    titleTemplate: "Fleet Integrations",
  },
  data() {
    return {
      windowHeight: window.innerHeight,
      details_fromDate: justDate(moment().add(-1, 'days')),
      details_toDate: justDate(moment()),
      RANGES,
      selected_tag: '',
      selected_vehicle: '',
      selected_status:'',
      selected_maint_status: '',
      search_filter: '',
      vehicle_options: [ { value: '', text: 'All Vehicles' }],
      updating: false,
      items: [],
      items_full: [],
      summary: null,
      loading: false,
      vehicle_tag_options: null,
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      alert: {
        show: false,
        message: '',
        variant: ''
      },
      status_options: [
        { value: '', text: 'All' },
        { value: 'READY', text: 'READY' },
        { value: 'DIRTY', text: 'DIRTY' },
        { value: 'CLEAN', text: 'CLEAN' },
        { value: 'WASH', text: 'WASHED' },
        { value: 'OUT', text: 'OUT' },
      ],
      status_chip_map: {
        ready: 'READY',
        dirty: 'DIRTY',
        cleaned: 'CLEAN',
        washed: 'WASH',
        out: 'OUT',
      },
      fields: [
        { key: 'vehicle_name', label: 'Vehicle', sortable: true, visible: true, thStyle: { width: "110px" }},
        { key: 'processed_time', label:'Update At', visible: true, sortable: true, thStyle: { width: "140px" }},
        { key: 'updated_by', label:'Updated By', visible: true, sortable: true, thStyle: { width: "140px" }},
        { key: 'vehicle_status', label:'Veh Status', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'maintenance_status', label:'Maint Status', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'booking_hash', label:'Booking', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'last_dirty_time', label:'Dirty', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'confirmed_status', label: 'Driver Confirm', sortable: true, visible: true, thStyle: { width: "120px" }},
        { key: 'confirmed_by', label: 'Confirmed By', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_time', label:'Cleaned', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_number', label:'Cleaned #', sortable: true, visible: true, thStyle: { width: "130px" }},
        { key: 'last_washed_time', label:'Washed', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_washed_number', label:'Washed #', sortable: true, visible: true, thStyle: { width: "130px" }},
        { key: 'last_ready_time', label:'Ready', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_returned_time', label: 'Last Return', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'dump_date', label:'Dump Date', visible: false, sortable: true, thStyle: { width: "140px" }},
        { key: 'detailed_date', label:'Detail Date', visible: false, sortable: true, thStyle: { width: "140px" }},
        { key: 'wrapped_date', label:'Wrapped Date', visible: false, sortable: true, thStyle: { width: "140px" }},
        { key: 'odometer', label:'Odometer', visible: false, sortable: true, thStyle: { width: "100px" }},
        { key: 'fuel_level', label:'Fuel', visible: false, sortable: true, thStyle: { width: "100px" }},
        { key: 'def_level', label:'DEF', visible: false, sortable: true, thStyle: { width: "100px" }},
        { key: 'wheelchair', label: 'WC', sortable: true, thStyle: { width: "75px" }},
        { key: 'stayanddo', label: 'S&D', sortable: true, thStyle: { width: "75px" }},
        { key: 'tote', label: 'Tote', sortable: true, thStyle: { width: "110px" }},
        { key: 'notes', label: 'Notes', sortable: true, visible: true, thStyle: { width: "220px" }},
        { key: 'changes', label: 'Changes (old \u2192 new)', visible: true, thStyle: { minWidth: "260px" }},
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment().add(0, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      }
    }
  },
  async created() {
    this.fields_all = this.fields
    await this.check_features()
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    isMobile() {
      return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    display_maint_status(status) {
      const features = this.user && this.user.features && this.user.features[this.user.tenant]
      return displayMaintStatus(status, features && features.vehicle_status_management_dvir_combined)
    },
    async check_features() {
      this.items = []
      this.summary = null
      this.load_tags()
      this.load_vehicles()
      await this.load_data()
    },
    async load_vehicles() {
      const {value: {data = []}} = await get_vehicle_list(this.user.tenant, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
      this.vehicle_options = this.name_the_all_entry(data, 'All Vehicles')
    },
    async load_tags() {
      const {value: {data = []}} = await get_tag_list(this.user.tenant)
      this.vehicle_tag_options = this.name_the_all_entry(data, 'All Tags')
    },
    // both lists arrive from the shared endpoints led by a plain "All", and the two
    // dropdowns sit side by side carrying no labels - so the toolbar read "All / All" and
    // said nothing about which list was which. Renamed here rather than at the endpoints,
    // which every other page's filters share.
    name_the_all_entry(options, label) {
      const list = (options || []).slice()
      if (list.length && !list[0].value)
        list[0] = Object.assign({}, list[0], { text: label })
      else
        list.unshift({ value: '', text: label })
      return list
    },
    showAlert(msg, variant, autoclose) {
      this.alert.show = true
      this.alert.message = msg
      this.alert.variant = variant
      autoclose
          ? setTimeout(() => this.closeAlert(), autoclose)
          : null
    },
    closeAlert() {
      this.alert.show = false
      this.alert.message = ''
      this.alert.variant = ''
    },
    tableSortCompare(aRow, bRow, key) {
      if (key == 'next_mvmt_delta') {
        return aRow['next_mvmt_ms'] - bRow['next_mvmt_ms']
      }
      return false;
    },
    async load_data() {
      this.loading = true
      try {
        // the chips carry per-status counts now, so the window has to come back whole -
        // a server-side status filter would leave every other chip counting its own
        // filtered result. The row filters run in filter_items() instead.
        const {value: { data = [] } } = await get_vehicle_status_history(this.user.tenant, this.selected_tag, this.selected_vehicle, '', '', moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))

        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        // every row learns what it changed by being diffed against the next-older row for
        // the same vehicle, which is also what makes "changed to DIRTY" a chip can mean
        this.items_full = annotateChanges(data.results, { allFields: true })
        this.items = this.items_full
        this.filter_items()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
    },
    set_range(value) {
      this.details_fromDate = justDate(moment().add(-value, 'days'))
      this.details_toDate = justDate(moment())
      this.$nextTick(() => this.load_data())
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/reports/vehiclestatus/history/download`,
          {
            tenant: this.user.tenant,
            tagName: this.selected_tag,
            vehicleName: this.selected_vehicle,
            filter: this.search_filter,
            statusFilter: this.selected_status,
            maintStatusFilter: this.selected_maint_status,
            startDate: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            endDate: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `vsm-history-${this.details_fromDate}-${this.details_toDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
    clear_history_filters() {
      this.selected_status = ''
      this.selected_maint_status = ''
      this.search_filter = ''
      this.filter_items()
    },
    filter_by_status(chipKey) {
      if (chipKey == null) {
        this.selected_status = ''
      } else {
        const val = this.status_chip_map[chipKey]
        this.selected_status = this.selected_status === val ? '' : val
      }
      this.filter_items()
    },
    filter_by_maint_status(value) {
      this.selected_maint_status = this.selected_maint_status === value ? '' : value
      this.filter_items()
    },
    // A chip means "the update that set this status", not "the status this row happens to
    // be sitting at" - otherwise every telemetry snapshot in between answers to it and the
    // count says far more happened than did. status_changed comes from the diff against the
    // next-older row for that vehicle, so a vehicle's oldest row in the window never counts:
    // nothing in range says whether it changed anything.
    //
    // The value test is the server's old one: a lowercase substring, because the chip values
    // are the stems of the stored words (CLEAN finds CLEANED, WASH finds WASHED).
    status_matches(item, status) {
      return !!item.status_changed && !!item.vehicle_status &&
          String(item.vehicle_status).toLowerCase().indexOf(status.toLowerCase()) != -1
    },
    maint_matches(item, status) {
      if (!item.maint_changed || !item.maintenance_status) return false
      if (status == 'DVIR')
        return item.maintenance_status == 'PENDING_DVIR' || item.maintenance_status == 'UNSAFE_DVIR'
      return item.maintenance_status == status
    },
    filter_items() {
      this.items = this.items_full
      if (this.selected_status != '') {
        this.items = this.items.filter(item => this.status_matches(item, this.selected_status))
      }
      if (this.selected_maint_status != '') {
        this.items = this.items.filter(item => this.maint_matches(item, this.selected_maint_status))
      }
      if (this.search_filter != '') {
        this.items = this.items.filter(item => (this.contains(item.vehicle_name, this.search_filter) || this.contains(item.updated_by, this.search_filter) || this.contains(item.vehicle_status, this.search_filter) || this.contains(item.notes, this.search_filter)))
      }
    },
    contains(val1, val2) {
      if (val1 != null && val2 != null) {
        return String(val1).toLowerCase().indexOf(String(val2).toLowerCase()) != -1;
      }
      return false;
    },

},
  computed:{
    // every count comes off the unfiltered window, so selecting one chip never moves
    // another chip's number
    status_counts() {
      const counts = { READY: 0, DIRTY: 0, CLEAN: 0, WASH: 0, OUT: 0 }
      for (const item of this.items_full) {
        for (const status of Object.keys(counts)) {
          if (this.status_matches(item, status)) counts[status] += 1
        }
      }
      return counts
    },
    maint_counts() {
      const counts = { READY: 0, PENDING_DVIR: 0, UNSAFE_DVIR: 0, DVIR: 0, PM: 0, OOS: 0 }
      for (const item of this.items_full) {
        for (const status of Object.keys(counts)) {
          if (this.maint_matches(item, status)) counts[status] += 1
        }
      }
      return counts
    },
    // tenants who do not track a maintenance status have the column empty throughout -
    // no point offering them a second chip row that filters everything to nothing
    show_maint_chips() {
      return this.items_full.some(item => item.maintenance_status)
    },
    dvir_combined() {
      const features = this.user && this.user.features && this.user.features[this.user.tenant]
      return !!(features && features.vehicle_status_management_dvir_combined)
    },
    // <input type="date"> speaks ISO while this page has always held MM-DD-YYYY;
    // translating here leaves load_data, download and the nudge arrows untouched
    from_date_iso: {
      get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) { this.details_fromDate = moment(v).format('MM-DD-YYYY'); this.load_data() } },
    },
    to_date_iso: {
      get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) { this.details_toDate = moment(v).format('MM-DD-YYYY'); this.load_data() } },
    },
    // a preset lights only when the loaded window happens to match it, so a hand-picked
    // range needs no mode of its own
    active_range_days() {
      if (this.to_date_iso !== moment().format('YYYY-MM-DD')) return null
      const days = moment(this.to_date_iso).diff(moment(this.from_date_iso), 'days')
      const match = RANGES.find(r => r.value !== 'custom' && r.value === days)
      return match ? match.value : null
    },
    ...mapState({
      user: state => state.profile.user
    }),
    visibleFields() {
      var custom_fields = null;

      if (this.summary && this.summary.visible_history_fields)
        custom_fields = this.summary.visible_history_fields;

      if (custom_fields != null) {
        // vehicle_name leads and changes trails whatever the tenant configured - the column
        // list predates both, so an existing setting would otherwise hide them
        custom_fields = "vehicle_name," + custom_fields + ",changes";
        let new_fields = []
        for (let visible_field of custom_fields.split(",").map(f => f.trim())) {
          let found_field = this.fields.filter(field => visible_field == field.key)
          if (found_field.length > 0) {
            new_fields.push(found_field[0]);
          }
        }
        return new_fields;
      } else
        return this.fields
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    selected_tag() {
      if (this.selected_tag != '')
        this.selected_vehicle = ''
    },
    selected_vehicle() {
      if (this.selected_vehicle != '')
        this.selected_tag = ''
    },
    search_filter() {
      this.filter_items()
    },
    details_fromDate(target_from_date) {
      this.details_fromDate = target_from_date
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = this.details_fromDate
    }
  }
}
</script>

<style scoped>
/* one change per line: the board's list lays them out side by side, which works for the
   two or three fields it diffs, but a status update here moves half a dozen at once */
.vsh-changes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
}

.vsh-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  gap: 8px;
  margin: 20px 8px 6px;
}


</style>

